import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { App } from 'antd'
import { MindmapService } from '../../../services/mindmap/mindmapService'
import { getProjectLabels, getNodeLabels } from '../../../services/labels/labelService'
import { useFeatureAccessContext } from '../../../context/featureAccess'
import type { MindMapRecord, ProjectLabel } from '@bandos/shared'

/**
 * Loads a mindmap and its associated labels from the API.
 * Shared between ReportPage and ValidationPlannerPage.
 */
export function useMindmapData(mindmapId: string | undefined) {
  const { notification: antdNotification } = App.useApp()
  const { isFeatureEnabled } = useFeatureAccessContext()
  const [loading, setLoading] = useState(true)
  const [mindmap, setMindmap] = useState<MindMapRecord | null>(null)
  const [projectLabels, setProjectLabels] = useState<ProjectLabel[]>([])
  const [nodeLabelMap, setNodeLabelMap] = useState<Map<string, string[]>>(new Map())

  useEffect(() => {
    const loadMindmap = async () => {
      if (!mindmapId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const { data, error } = await MindmapService.get(mindmapId)

        if (error) {
          antdNotification.error({
            title: 'Failed to load mindmap',
            description: error.message,
          })
          setLoading(false)
          return
        }

        if (!data) {
          antdNotification.error({
            title: 'Mindmap not found',
            description: 'The requested mindmap could not be found',
          })
          setLoading(false)
          return
        }

        setMindmap(data)
      } catch (error) {
        Sentry.captureException(error, {
          tags: { feature: 'report', action: 'load_mindmap' },
        })
        antdNotification.error({
          title: 'Error',
          description: 'An unexpected error occurred while loading the mindmap',
        })
      } finally {
        setLoading(false)
      }
    }

    void loadMindmap()
  }, [mindmapId, antdNotification])

  // Load labels and node-label mappings (only when labels feature is enabled)
  const labelsEnabled = isFeatureEnabled('labels')
  useEffect(() => {
    if (!labelsEnabled) {
      setProjectLabels([])
      setNodeLabelMap(new Map())
      return
    }

    const loadLabels = async () => {
      if (!mindmapId || !mindmap) return

      try {
        const { data: labelsData, error: labelsError } = await getProjectLabels(mindmapId)
        if (labelsError || !labelsData) return

        setProjectLabels(labelsData.labels)

        const labelMap = new Map<string, string[]>()
        await Promise.all(
          mindmap.content.nodes.map(async (node) => {
            const { data: nodeLabelsData } = await getNodeLabels(mindmapId, node.id)
            if (nodeLabelsData?.labels) {
              labelMap.set(
                node.id,
                nodeLabelsData.labels.map((label) => label.id)
              )
            }
          })
        )

        setNodeLabelMap(labelMap)
      } catch (error) {
        console.warn('[useMindmapData] failed to load node labels', error)
        Sentry.captureException(error, {
          tags: { feature: 'report', action: 'load_node_labels' },
        })
      }
    }

    void loadLabels()
  }, [mindmapId, mindmap, labelsEnabled])

  return {
    loading,
    mindmap,
    setMindmap,
    projectLabels,
    nodeLabelMap,
  }
}
